import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { useAnalytics } from "../context/AnalyticsContext";
import { cryptiqueAnalyticsTheme as theme } from "../theme";
import Icon from "../../../../shared/components/Icon";
import EventsTimeFilter from "../../../conversion-events/components/EventsTimeFilter";

/** Preset -> interval for board time override. Use "day" for all so the chart gets full date range; "month" would only return buckets that have data (sparse months missing). */
const PRESET_INTERVAL = {
  Today: "day",
  Yesterday: "day",
  "7D": "day",
  "30D": "day",
  "3M": "day",
  "6M": "day",
  "12M": "day",
};

/** Normalize custom from/to to UTC day boundaries for backend (same as InsightsEditorPage). */
function normalizeCustomRangeToUtcDays(fromIso, toIso, enableTimeRanges) {
  if (enableTimeRanges) return { start: fromIso, end: toIso };
  const toLocalYmd = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };
  const fromYmd = toLocalYmd(fromIso);
  const toYmd = toLocalYmd(toIso);
  if (!fromYmd || !toYmd) return { start: fromIso, end: toIso };
  const nextDayYmd = (ymd) => {
    const d = new Date(ymd + "T12:00:00.000Z");
    d.setUTCDate(d.getUTCDate() + 1);
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
  };
  return {
    start: fromYmd + "T00:00:00.000Z",
    end: nextDayYmd(toYmd) + "T00:00:00.000Z",
  };
}

/**
 * @param {string} preset
 * @returns {{ mode: 'preset', preset: string, start: null, end: null, compare: null, interval: string, timezone: string }}
 */
function presetToBoardTime(preset) {
  return {
    mode: "preset",
    preset,
    start: null,
    end: null,
    compare: null,
    interval: PRESET_INTERVAL[preset] || "day",
    timezone: "UTC",
  };
}

/**
 * Board mode: boardTime + onBoardTimeChange are provided, and we show Default + presets.
 * Global mode: use AnalyticsContext (e.g. Reports list).
 */
export default function TimeRangeFilters({ boardTime, onBoardTimeChange }) {
  const { globalTimeRange, setGlobalTimeRange, TIME_RANGES } = useAnalytics();
  const [customPopoverOpen, setCustomPopoverOpen] = useState(false);
  const [customPopoverRect, setCustomPopoverRect] = useState(null);
  const customButtonRef = useRef(null);

  const isBoardMode = boardTime != null && typeof onBoardTimeChange === "function";

  const ranges = isBoardMode ? ["Default", "Custom", ...TIME_RANGES.filter((r) => r !== "Custom")] : TIME_RANGES;

  const getSelectedValue = () => {
    if (isBoardMode) {
      if (boardTime.mode === "default") return "Default";
      if (boardTime.mode === "custom") return "Custom";
      return boardTime.preset || "30D";
    }
    return globalTimeRange;
  };

  const selectedValue = getSelectedValue();

  const handleBoardCustomApply = (filter) => {
    if (filter.type === "custom" && filter.from && filter.to) {
      const { start, end } = normalizeCustomRangeToUtcDays(
        filter.from,
        filter.to,
        filter.enableTimeRanges === true
      );
      onBoardTimeChange({
        mode: "custom",
        preset: null,
        start,
        end,
        compare: null,
        interval: "day",
        timezone: "UTC",
      });
    }
    setCustomPopoverOpen(false);
  };

  const handleClick = (range) => {
    if (isBoardMode) {
      if (range === "Default") {
        onBoardTimeChange({ mode: "default" });
        return;
      }
      if (range === "Custom") {
        if (customButtonRef.current) {
          const rect = customButtonRef.current.getBoundingClientRect();
          setCustomPopoverRect({ left: rect.left, top: rect.bottom + 8 });
        }
        setCustomPopoverOpen(true);
        return;
      }
      onBoardTimeChange(presetToBoardTime(range));
      return;
    }
    setGlobalTimeRange(range);
  };

  useEffect(() => {
    if (!customPopoverOpen) return;
    const onDocClick = (e) => {
      if (
        customButtonRef.current?.contains(e.target) ||
        e.target.closest?.("[data-board-custom-popover]")
      )
        return;
      setCustomPopoverOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [customPopoverOpen]);

  return (
    <div
      className="flex items-center px-4 py-2 border-b"
      style={{
        fontFamily: "Raleway, sans-serif",
        backgroundColor: "#ffffff",
        borderColor: theme.border,
      }}
    >
      <div className="flex items-center relative">
        {ranges.map((range, index) => (
          <button
            key={range}
            ref={range === "Custom" && isBoardMode ? customButtonRef : undefined}
            type="button"
            onClick={() => handleClick(range)}
            title={range === "Custom" ? "Custom date range" : range === "Default" ? "Use each card's saved time" : undefined}
            className={`px-3 py-1 text-sm font-medium transition-colors ${
              index === 0 ? "rounded-l-lg" : ""
            } ${
              index === ranges.length - 1 ? "rounded-r-lg" : ""
            } ${
              selectedValue === range
                ? "bg-gray-100 text-[#0f172a] border border-gray-300 z-10"
                : "bg-white text-[#475569] hover:bg-gray-50 border border-gray-300"
            } ${index > 0 ? "-ml-px" : ""}`}
            style={{ fontFamily: "Raleway, sans-serif" }}
          >
            {range === "Custom" && (
              <Icon name="calendar" size={18} color="currentColor" style={{ display: "inline", marginRight: "4px", verticalAlign: "middle" }} />
            )}
            {range}
          </button>
        ))}
        {isBoardMode && customPopoverOpen && typeof document !== "undefined" && document.body && createPortal(
          <div
            data-board-custom-popover
            className="fixed z-[100]"
            style={{
              left: customPopoverRect?.left ?? 0,
              top: customPopoverRect?.top ?? 0,
              fontFamily: "Raleway, sans-serif",
            }}
          >
            <EventsTimeFilter
              selectedPreset="custom"
              openCustomOnMount={true}
              hidePresetButtons={true}
              onTimeFilterChange={handleBoardCustomApply}
              onClose={() => setCustomPopoverOpen(false)}
            />
          </div>,
          document.body
        )}
      </div>
    </div>
  );
}
